import { useEffect, useState } from "react";
import { useParams, Link, useLocation, Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import RateCalculator from "@/components/RateCalculator";
import BrandIcon from "@/components/BrandIcon";
import { ArrowRight, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/brand";
import { SELL_BRAND_RE } from "@/lib/routes";

export default function SellBrandPage() {
  const params = useParams();
  const { pathname } = useLocation();
  const match = pathname.match(SELL_BRAND_RE);
  const slug = params.slug || (match && match[1]);

  const [brand, setBrand] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api
      .get(`/brands/${slug}`)
      .then((r) => setBrand(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (!slug || notFound) return <Navigate to="/accepted-cards" replace />;
  if (!brand) {
    return <div className="max-w-3xl mx-auto px-5 py-20 text-[#9a93a6]">Loading…</div>;
  }

  const topRate = brand.rates?.receipt ?? brand.rate;
  const stdRate = brand.rates?.no_receipt ?? brand.rate;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `Sell ${brand.name} gift card`,
    provider: { "@type": "Organization", name: BRAND.name },
    description: `Convert your ${brand.name} gift card into cash. Up to ${topRate}% payout.`,
  };

  return (
    <>
      <SEO
        title={`Sell ${brand.name} gift card — up to ${topRate}%`}
        description={`Sell your ${brand.name} gift card. Published rate up to ${topRate}% on verified balances (with receipt). ${stdRate}% standard. Pick PayPal, Cash App, Zelle, or Chime.`}
        schema={schema}
      />
      <section className="search-home-hero-section" data-testid={`sell-brand-hero-${brand.slug}`} style={{ paddingBottom: 40 }}>
        <div className="main-container w-container">
          <div style={{ display: "inline-flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
            <span style={{ width: 54, height: 54, borderRadius: 16, background: brand.color, overflow: "hidden", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
              <BrandIcon logoKey={brand.logo_key} name={brand.name} className="w-full h-full" />
            </span>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "#346af8" }}>{brand.category}</div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "#0b0d17" }}>{brand.name}</div>
            </div>
          </div>
          <h1 className="h1 _700-bold" style={{ maxWidth: 900 }}>
            Sell {brand.name} gift cards for up to <span style={{ color: "#346af8" }}>{topRate}%</span>
          </h1>
          <p className="main-body-text _500-medium" style={{ marginTop: 18, color: "#4a4d5f", maxWidth: 620 }}>
            Convert your unused {brand.name} card into a payout via PayPal, Zelle, Cash App, or Chime. Up to {topRate}% with the original receipt; {stdRate}% standard rate.
          </p>
          <div style={{ marginTop: 26, display: "flex", flexWrap: "wrap", gap: 12 }}>
            <Link to={`/submit?brand=${brand.slug}`} data-testid={`sell-brand-cta-${brand.slug}`} className="primary-button w-button">
              Sell my {brand.name} card <ArrowRight className="w-4 h-4" style={{ display: "inline", marginLeft: 4, verticalAlign: "middle" }} />
            </Link>
            <Link to="/rates" className="btn w-inline-block"><div className="btn-text">See other brand rates</div></Link>
          </div>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-20 grid lg:grid-cols-12 gap-10 items-start" style={{ paddingTop: 20 }}>
        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-3 gap-4">
            <Stat icon={ShieldCheck} k="Review" v="Human" />
            <Stat icon={Clock} k="Fees" v="None" />
            <Stat icon={CheckCircle2} k="Signup" v="Not needed" />
          </div>

          <div className="mt-12">
            {brand.seo_long_form ? (
              <article
                data-testid="brand-seo-longform"
                className="brand-longform"
                dangerouslySetInnerHTML={{ __html: brand.seo_long_form }}
              />
            ) : (
              <>
                <h2 className="font-display text-2xl font-semibold text-[#0b0d17]">
                  About {brand.name} cards
                </h2>
                <p className="mt-3 text-[#6c6376] leading-relaxed">
                  We accept {brand.name} gift cards from <span className="font-mono-display">${brand.min}</span> to <span className="font-mono-display">${brand.max}</span>. Our published rates: <strong>{topRate}%</strong> with the original receipt, <strong>{stdRate}%</strong> without. Partial-balance cards pay {brand.rates?.partial ?? stdRate}%. Submissions are reviewed by a person before approval.
                </p>
                <h3 className="font-display text-lg font-semibold text-[#0b0d17] mt-6">
                  How to sell your {brand.name} gift card
                </h3>
                <ol className="mt-3 space-y-2 text-[#6c6376] list-decimal list-inside">
                  <li>Enter your card balance to see your exact payout.</li>
                  <li>Submit the card number, PIN if applicable, and a photo.</li>
                  <li>Choose PayPal, Cash App, Zelle, or Chime.</li>
                  <li>Receive your payout once we approve the card.</li>
                </ol>
              </>
            )}
          </div>
        </div>
        <div className="lg:col-span-5">
          <RateCalculator />
        </div>
      </section>
    </>
  );
}

function Stat({ icon: Icon, k, v }) {
  return (
    <div className="bg-white border border-[#e8e8ef] rounded-xl p-4 soft-shadow">
      <div className="w-8 h-8 rounded-lg bg-[#eaf0ff] text-[#346af8] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-2 font-display text-2xl font-bold text-[#0b0d17]">{v}</div>
      <div className="text-xs uppercase tracking-wider text-[#9a93a6]">{k}</div>
    </div>
  );
}
